import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from '@orion-platform/core';
import { Column } from '@orion-platform/datagrid';
import { environment } from '@orion/environment';
import { ApiQueryParams, QueryPayload } from 'apps/orion/src/app/models/app-config.model';
import { Observable } from 'rxjs';
import {
  Carrier,
  CarrierContact,
  CarrierResult,
  CarriersDetail,
  CarriersListField,
  CreateCarrierLogistic,
  CreateCarrierPayload,
  DeleteCarrierLogisticsResult,
  EnumCarriersFieldStructure,
} from '../models/carriers-list.model';
import {
  CARRIERS_COLUMN_LIST,
  CARRIERS_FIELDS_KEY,
} from '../pages/carriers-list/carriers-list.consts';
import {
  CarrierLogisticsGroup,
  CreateTransportResponse,
  CreateTransportType,
  DeleteTransportType,
  FormSetting,
  TransportDetails,
} from '../store/carriers-list/carriers-list.types';
import { DataGridResult } from '../store/partnumbers-list/partnumbers-list.types';
import { ColumnsState } from '../types/columns.types';
import { formatParams, generateHttpParams } from '../utils/general.utils';
import { ColumnsService } from './columns.service';
import { DefaultFormSettingsService } from './default-form-settings.service';

@Injectable({
  providedIn: 'root',
})
export class CarriersListService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly defaultForms = inject(DefaultFormSettingsService);
  private readonly columns = inject(ColumnsService);

  /**
   * The plant's allowed fields, merged with whatever the user saved.
   * @returns {Observable<ColumnsState>} The columns, and whether they are the defaults.
   */
  getDefaultColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new CarriersListField(EnumCarriersFieldStructure.CarriersTable, formFields)
          .fields as Column[],
      columnListKey: CARRIERS_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(CARRIERS_FIELDS_KEY),
    });

  private readonly mm = environment.modulesBaseUrl.materialManagement;
  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;

  private readonly carriersUrl = `${this.baseUrl}${this.mm.carriers}`;
  private readonly contactsUrl = `${this.baseUrl}${this.mm.contacts}`;

  getCarriersList(data?: ApiQueryParams): Observable<DataGridResult<CarriersDetail[]>> {
    const params = formatParams(data);
    return this.http.get<DataGridResult<CarriersDetail[]>>(this.carriersUrl, { params });
  }

  createCarrier(carrier: CreateCarrierPayload): Observable<CarrierResult> {
    return this.http.post<CarrierResult>(this.carriersUrl, carrier);
  }

  editCarrier(carrier: Carrier): Observable<CarrierResult> {
    return this.http.put<CarrierResult>(`${this.carriersUrl}/${carrier.id}`, carrier);
  }

  deleteCarrier(id: string): Observable<CarrierResult> {
    return this.http.delete<CarrierResult>(`${this.carriersUrl}/${id}`);
  }

  getCarriersContacts(data: { key: string; value: string }): Observable<CarrierContact[]> {
    const params = generateHttpParams({ key: data.key, value: data.value });
    return this.http.get<CarrierContact[]>(this.contactsUrl, { params });
  }

  getCarrierLogistics(data: QueryPayload): Observable<CarrierLogisticsGroup[]> {
    const params = generateHttpParams({ $filter: data.$filter ?? '' });
    return this.http.get<CarrierLogisticsGroup[]>(`${this.carriersUrl}/contacts`, { params });
  }

  createCarrierLogistics(payload: CreateCarrierLogistic): Observable<CarrierResult> {
    return this.http.post<CarrierResult>(`${this.carriersUrl}/contacts`, payload);
  }

  deleteCarrierLogistics(id: string): Observable<DeleteCarrierLogisticsResult> {
    return this.http.delete<DeleteCarrierLogisticsResult>(`${this.carriersUrl}/contacts/${id}`);
  }

  getCarrierTransports(name: string): Observable<TransportDetails[]> {
    const params = generateHttpParams({ name });
    return this.http.get<TransportDetails[]>(`${this.carriersUrl}/TypeOfTransports`, { params });
  }

  createCarrierTransport(payload: CreateTransportType): Observable<CreateTransportResponse> {
    return this.http.post<CreateTransportResponse>(`${this.carriersUrl}/TypeOfTransports`, payload);
  }

  deleteCarrierTransport(id: string): Observable<DeleteTransportType> {
    return this.http.delete<DeleteTransportType>(`${this.carriersUrl}/TypeOfTransports/${id}`);
  }

  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    return this.defaultForms.getFields(this.carriersUrl, formKey);
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    return this.defaultForms.formKeys(this.carriersUrl);
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    return this.defaultForms.getFormSettings(this.carriersUrl, formKey);
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    return this.defaultForms.update(this.carriersUrl, formKey, payload);
  }

  /**
   * Which fields the create form shows, as the user arranged them.
   *
   * The same list in the same shape as a grid's columns, stored the same way —
   * only what draws it differs.
   * @returns {Observable<ColumnsState>} The fields, and whether they are still the defaults.
   */
  getFormFieldColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new CarriersListField(EnumCarriersFieldStructure.CarriersCreateForm, formFields)
          .fields as unknown as Column[],
      columnListKey: 'carriers_field_list',
      formFields$: this.getDefaultFormSettingFields('CARRIERS_DETAIL_FORMKEY'),
    });
}
